import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';


import { CircleCheck, CloudDownload, Download, EllipsisVertical, EyeOff, Flag, FlagOff, ListMinus, ListPlus, Loader, MoreHorizontal, PencilLine, Unlock } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import { Invoice, InvoiceLineItem } from 'src/redux/billing/billing.types';
import { useSyncInvoiceMutation } from 'src/redux/billing/billing.endpoints';
import { useInvoiceDownload } from '../hooks/use-invoice-download';
import { getInvoiceDownloadDisabledReason } from '../utils/invoice-download-response';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'src/components/ui/dialog';
import FilteredInputDropdown from 'src/components/ui/filtered-input-dropdown';
import { Company } from 'src/pages/billing/types/billing.types';
import { canSyncInvoice, canUnlockInvoice } from '../utils/billing-utils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/redux-store';
import { showNotification } from 'src/redux/notification/notification.slice';

interface InvoiceOptionsDropdownProps {
  disabled: boolean;
  invoice: Invoice;
  selectedGroupId: string;
  updateInvoiceField: (invoiceId: string, field: string, value: any) => void;
}

interface InvoiceLineItemAssignmentModalProps {
  invoiceLineItem: InvoiceLineItem;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  invoices: Invoice[];
  updateLineItemField: (lineItemId: string, field: string, value: any) => void;
  companies: Company[];
}

const InvoiceLineItemAssignmentModal: React.FC<InvoiceLineItemAssignmentModalProps> = ({
  invoiceLineItem,
  isOpen,
  onOpenChange,
  invoices,
  companies,
  updateLineItemField,
}) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  const getCompanyName = (companyId: string): string => {
    const company = companies.find((company) => company.id === companyId);
    return company?.company_name || companyId;
  };

  const handleReassign = () => {
    if (selectedCompanyId !== invoiceLineItem.invoice) {
      updateLineItemField(invoiceLineItem.id, 'invoice', selectedCompanyId);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle>
            Assign to Invoice
          </DialogTitle>
          <DialogDescription>
            Assign the line item to a new invoice.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <FilteredInputDropdown
              inputClassName='bg-gray-300 rounded-xl'
              options={invoices?.map((invoice) => ({
                value: invoice.id,
                label: getCompanyName(invoice.company),
              })) || []}
              selectedValue={selectedCompanyId || undefined}
              onSelect={(value: string) => {
                setSelectedCompanyId(value);
              }}
              placeholder="Search for a company..."
            />

          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant='create'
            onClick={handleReassign}
          >
            Reassign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const InvoiceOptionsDropdown: React.FC<InvoiceOptionsDropdownProps> = ({
  disabled,
  invoice,
  selectedGroupId,
  updateInvoiceField,
}) => {
  const dispatch = useDispatch();
  const [syncInvoice] = useSyncInvoiceMutation();
  const { downloadInvoicePdf, isInvoiceDownloading } = useInvoiceDownload();
  const userState = useSelector((state: RootState) => state.userState);
  const isBillingAdmin = canSyncInvoice(userState.permissions?.groups || []);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const isPendingSync = invoice?.invoice_status?.toLowerCase() === 'pending_sync';
  const isSynced = invoice?.invoice_status?.toLowerCase() === 'synced';
  const downloadDisabledReason = getInvoiceDownloadDisabledReason(invoice);
  const isDownloading = isInvoiceDownloading(invoice.id);
  const isDownloadDisabled = Boolean(downloadDisabledReason) || isDownloading;

  const handleSyncInvoice = async() => {
    if (!invoice) return;
    if (isPendingSync) return;
    try {
      const result = await syncInvoice({ id: invoice.id }).unwrap();
      const summary =
        typeof result === 'string' && result.trim() !== ''
          ? result
          : 'Invoice synced successfully';
      dispatch(showNotification({ summary, theme: 'success' }));
    } catch {
      dispatch(showNotification({ summary: 'Sync failed. Please try again.', theme: 'warning' }));
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            disabled={disabled}
            variant="ghost"
            className="h-8 w-8 p-0 rounded-full hover:bg-gray-500 hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <span className="sr-only">
              Open menu
            </span>
            <EllipsisVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">

          {/* flag */}
          {!invoice.attention_needed ? (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                updateInvoiceField(invoice.id, 'attention_needed', true);
              }}
              className="cursor-pointer"
            >
              <Flag className="h-4 w-4" />
              Attention Needed
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                updateInvoiceField(invoice.id, 'attention_needed', false);
              }}
              className="cursor-pointer"
            >
              <FlagOff className="h-4 w-4" />
              Resolved
            </DropdownMenuItem>
          )}


          {/* hide */}
          {invoice.hide ? (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                updateInvoiceField(invoice.id, 'hide', false);
              }}
              className="cursor-pointer"
            >
              <EyeOff className="h-4 w-4" />
              Show
            </DropdownMenuItem>
          ) : (
          // show
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                updateInvoiceField(invoice.id, 'hide', true);
              }}
              className="cursor-pointer"
            >
              <EyeOff className="h-4 w-4" />
              Hide
            </DropdownMenuItem>
          )}

          {canUnlockInvoice(invoice, userState.permissions?.groups || []) && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                updateInvoiceField(invoice.id, 'invoice_status', 'draft');
              }}
              className="cursor-pointer"
            >
              <Unlock className="h-4 w-4" />
              Unlock
            </DropdownMenuItem>
          )}

          {/* sync — all users; non-admins confirm in a modal first */}
          {!isSynced && (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (isPendingSync) return;
                if (isBillingAdmin) {
                  void handleSyncInvoice();
                } else {
                  setSyncConfirmOpen(true);
                }
              }}
              disabled={isPendingSync}
              className={isPendingSync ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
            >
              <CloudDownload className="h-4 w-4" />
              Sync
            </DropdownMenuItem>
          )}

          {invoice.invoice_status?.toLowerCase() === 'approved' ? (
          // draft
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                updateInvoiceField(invoice.id, 'invoice_status', 'draft');
              }}
              className="cursor-pointer"
            >
              <PencilLine className="h-4 w-4" />
              Set as Draft
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                updateInvoiceField(invoice.id, 'invoice_status', 'approved');
              }}
              className="cursor-pointer"
            >
              <CircleCheck className="h-4 w-4" />
              Approve
            </DropdownMenuItem>
          )}

          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (isDownloadDisabled) {
                return;
              }
              void downloadInvoicePdf(invoice.id);
            }}
            disabled={isDownloadDisabled}
            aria-busy={isDownloading}
            title={downloadDisabledReason ?? undefined}
            className={isDownloadDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
          >
            {isDownloading ? (
              <Loader className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="h-4 w-4" aria-hidden="true" />
            )}
            Download
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={syncConfirmOpen}
        onOpenChange={setSyncConfirmOpen}
      >
        <DialogContent
          className="sm:max-w-md bg-white"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>
              Confirm sync
            </DialogTitle>
            <DialogDescription>
              Please confirm with everyone that this invoice is ready to sync. After syncing, the invoice is locked for editing and only billing administrators can change it. Syncing before the invoice is ready can delay billing.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                setSyncConfirmOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="create"
              onClick={(e) => {
                e.stopPropagation();
                setSyncConfirmOpen(false);
                void handleSyncInvoice();
              }}
            >
              Sync invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface InvoiceLineItemOptionsDropdownProps {
  lineItem: InvoiceLineItem;
  updateLineItemField: (lineItemId: string, field: string, value: any) => void;
  invoices: Invoice[];
  companies: Company[];
}

const InvoiceLineItemOptionsDropdown: React.FC<InvoiceLineItemOptionsDropdownProps> = ({
  lineItem,
  updateLineItemField,
  invoices,
  companies,
}) => {

  const [isShow, setIsShow] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleUnassignLineItem = (lineItem: InvoiceLineItem) => {
    const unassignedCompany = companies.find(company => company.company_name === 'Unassigned');
    const unassignedInvoice = invoices.find(invoice => invoice.company === unassignedCompany?.id);
    if (unassignedInvoice) {
      updateLineItemField(lineItem.id, 'invoice', unassignedInvoice?.id);
    }
  };

  return (
    <DropdownMenu
      open={menuOpen}
      onOpenChange={setMenuOpen}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <span className="sr-only">
            Open menu
          </span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* unassign */}
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleUnassignLineItem(lineItem);
          }}
          className="cursor-pointer"
        >
          <ListMinus className="h-4 w-4" />
          Unassign
        </DropdownMenuItem>


        {/* reassign */}
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(false);
            setIsShow(true);
          }}
          className="cursor-pointer"
        >
          <ListPlus className="h-4 w-4" />
          Assign to
        </DropdownMenuItem>

        {/* flag */}
        {!lineItem.attention_needed ? (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'attention_needed', true);
            }}
            className="cursor-pointer"
          >
            <Flag className="h-4 w-4" />
            Attention Needed
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'attention_needed', false);
            }}
            className="cursor-pointer"
          >
            <FlagOff className="h-4 w-4" />
            Resolved
          </DropdownMenuItem>
        )}


        {/* hide */}
        {lineItem.hide ? (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'hide', false);
            }}
            className="cursor-pointer"
          >
            <EyeOff className="h-4 w-4" />
            Show
          </DropdownMenuItem>
        ) : (
          // show
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'hide', true);
            }}
            className="cursor-pointer"
          >
            <EyeOff className="h-4 w-4" />
            Hide
          </DropdownMenuItem>
        )}

        {lineItem.invoice_line_item_status?.toLowerCase() === 'approved' ? (
          // draft
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'invoice_line_item_status', 'draft');
            }}
            className="cursor-pointer"
          >
            <PencilLine className="h-4 w-4" />
            Set as Draft
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              updateLineItemField(lineItem.id, 'invoice_line_item_status', 'approved');
            }}
            className="cursor-pointer"
          >
            <CircleCheck className="h-4 w-4" />
            Approve
          </DropdownMenuItem>
        )}

      </DropdownMenuContent>

      <InvoiceLineItemAssignmentModal
        invoiceLineItem={lineItem}
        isOpen={isShow}
        onOpenChange={(val) => setIsShow(val)}
        invoices={invoices}
        updateLineItemField={updateLineItemField}
        companies={companies}
      />
    </DropdownMenu>
  );
};

export { InvoiceOptionsDropdown, InvoiceLineItemOptionsDropdown };


